import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2 } from 'lucide-react';
import { usePlans, useStripeCheckout } from '@/hooks/useStripeBilling';
import { useAuthContext } from '@/contexts/AuthContext';
import { formatarMoeda } from '@/lib/currency';

export function PlanCards() {
  const { t } = useTranslation();
  const [isAnnual, setIsAnnual] = useState(false);
  const { data: plans, isLoading } = usePlans();
  const checkoutMutation = useStripeCheckout();
  const { organization } = useAuthContext();

  if (isLoading || !plans) return null;

  const upgradePlans = plans.filter(
    (p) => p.slug === 'professional' || p.slug === 'business',
  );

  const currentPlano = organization?.plano ?? 'starter';

  return (
    <div className="flex flex-col gap-5">
      {/* Toggle Mensal / Anual em pill segmentado (mesmo padrão da Agenda/Dashboard) */}
      <div className="flex justify-center">
        <div className="inline-flex p-1 rounded-full bg-muted/60 gap-1">
          <button
            type="button"
            onClick={() => setIsAnnual(false)}
            className={`px-4 py-1 rounded-full text-xs font-semibold transition-colors ${
              !isAnnual
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('billing.monthly')}
          </button>
          <button
            type="button"
            onClick={() => setIsAnnual(true)}
            className={`px-4 py-1 rounded-full text-xs font-semibold transition-colors inline-flex items-center gap-1.5 ${
              isAnnual
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('billing.annual')}
            <span
              className="text-[10px] font-mono px-1.5 py-0.5 rounded-full"
              style={{
                background: isAnnual ? 'hsl(var(--foreground))' : 'hsl(var(--muted))',
                color: isAnnual ? 'hsl(var(--background))' : 'hsl(var(--muted-foreground))',
              }}
            >
              {t('billing.annualDiscount')}
            </span>
          </button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {upgradePlans.map((plan) => {
          const priceId = isAnnual
            ? plan.stripe_price_yearly_id
            : plan.stripe_price_monthly_id;
          const price = isAnnual ? plan.preco_anual : plan.preco_mensal;
          const monthlyEquivalent =
            isAnnual && plan.preco_anual ? plan.preco_anual / 12 : plan.preco_mensal;

          const features = (t(`billing.features.${plan.slug}`, { returnObjects: true }) ??
            []) as string[];
          const isCurrent = currentPlano === plan.slug;
          const isDowngrade =
            currentPlano === 'business' && plan.slug === 'professional';

          // Plan card visual: branco com border-soft. Plano actual ganha
          // border ink (em vez de --primary teal/colorido).
          const cardClass = isCurrent
            ? 'ow-card border-foreground'
            : 'ow-card';

          return (
            <div key={plan.slug} className={cardClass}>
              <div className="p-6 flex flex-col gap-4">
                <div className="flex items-center justify-between gap-2">
                  <h4 className="text-base font-bold tracking-tight">{plan.nome}</h4>
                  {isCurrent && (
                    <span className="status-pill status-pill--ink">
                      {t('billing.currentPlanButton')}
                    </span>
                  )}
                </div>

                <div>
                  <p className="text-3xl font-bold tabular-nums leading-none">
                    {formatarMoeda(monthlyEquivalent ?? 0)}
                    <span className="text-sm font-normal text-muted-foreground ml-1">
                      {t('common.perMonth')}
                    </span>
                  </p>
                  {isAnnual && price !== null && (
                    <p className="text-xs text-muted-foreground mt-2">
                      {t('billing.billedAnnually', { amount: formatarMoeda(price ?? 0) })}
                    </p>
                  )}
                </div>

                <ul className="flex flex-col gap-2 py-2">
                  {features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm">
                      <span
                        className="flex-shrink-0 w-4 h-4 rounded-full bg-foreground flex items-center justify-center mt-0.5"
                        aria-hidden
                      >
                        <Check className="h-2.5 w-2.5 text-background" strokeWidth={3} />
                      </span>
                      <span className="text-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  className="ow-btn ow-btn--primary ow-btn--block"
                  disabled={
                    isCurrent ||
                    isDowngrade ||
                    !priceId ||
                    checkoutMutation.isPending
                  }
                  onClick={() => {
                    if (priceId) checkoutMutation.mutate({ price_id: priceId });
                  }}
                >
                  {checkoutMutation.isPending && (
                    <Loader2 size={14} className="animate-spin" />
                  )}
                  {isCurrent
                    ? t('billing.currentPlanButton')
                    : isDowngrade
                      ? t('billing.usePortalToChange')
                      : t('billing.subscribe', { plan: plan.nome })}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
